import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import createReducer from '../../createReducer';
import {
  startOrderRequest,
  phoneChange,
  customerDetailsDone,
  updateCustomer,
  deliveryDetailsDone,
  updateDeliveryAddress,
  updateDeliveryType,
  cartUpsert,
  clearFilters,
  menuQuery,
  orderItemChangeCount,
  startOrderError,
  startOrderSuccess,
  toggleMenuFilter,
  cancelOrder,
  fetchOrderHistorySuccess,
  setPaymentAssociations,
  addComment,
  setManagerDiscount,
  changeDeliveryFee,
  setCommentsServerTimestamp,
  setHasUnsavedComments,
  changeOrderComment, resetManagerDiscount
} from '../actions/OrderActions';
import { changeCount } from '../../../utils/DishUtils';
import { pushItem, removeItem, replaceItem } from '../../../utils/reducerUtils';
import { setUnavailableItems } from '../actions/OrderHistoryActions';
import { CustomerComment } from '../../../utils/CheckoutUtils';
import { getLatestComment } from '../../../utils/OrderUtils';
import { DiscountUnits, EMPTY_ADDRESS } from '../../../utils/constants';
import { createManagerDiscount } from './OrderReducer.helper';
import { clearCustomerSession } from '../actions/SessionActions';

let contactCounter = 0;
let addressesCounter = 0;

const initialState = {
  query: '',
  filters: {},
  callerInfo: {},
  phone: '',
  error: null,
  customer: null,
  comment: '',
  address: null,
  deliveryType: null,
  orderItems: [],
  unavailableOrderItems: [],
  orderHistory: [],
  paymentAssociations: [],
  comments: [],
  hasUnsavedComments: false,
  manualDeliveryFee: {
    enabled: false,
    amount: 0,
  },
  managerDiscount: { amount: 0, unit: DiscountUnits.CURRENCY, currencyAmount: 0, percentAmount: 0 },
  commentsServerTimestamp: 0,
};

const newCustomerState = initialState;

const NEW_DISH_INDEX = -1;

const handlers = {
  [changeDeliveryFee]: (state, payload) => {
    return {
      ...state,
      manualDeliveryFee: {
        enabled: true,
        amount: payload,
      },
    };
  },

  [phoneChange]: (state, payload) => {
    return {
      ...state,
      phone: payload.phone,
    };
  },

  [startOrderSuccess]: (state, payload) => {
    const { callerInfo, comments, commentsServerTimestamp } = payload;
    let customer = state.customer;
    let address = EMPTY_ADDRESS;

    if (callerInfo.contacts && callerInfo.contacts.length) {
      callerInfo.contacts = callerInfo.contacts.map(contact => ({ ...contact, id: contact.id || contactCounter++ }));
      customer = callerInfo.contacts[0];
    }

    if (callerInfo.addresses && callerInfo.addresses.length) {
      callerInfo.addresses = callerInfo.addresses.map(item => ({ ...item, id: item.id || addressesCounter++ }));
      address = callerInfo.addresses[0];
    }

    return { ...state, callerInfo: payload.callerInfo, customer, address, comments, commentsServerTimestamp };
  },
  [startOrderRequest]: (state, payload) => {
    return {
      ...state,
      error: null,
      customer: {
        phone: payload.phone,
      },
    };
  },
  [startOrderError]: state => {
    return {
      ...state,
      error: {
        type: 'phone',
      },
    };
  },
  [updateCustomer]: (state, payload) => {
    const { customer } = payload;
    const { callerInfo } = state;
    const contacts = get(callerInfo, 'contacts', []);
    const customerWithId = customer.id ? customer : { id: contacts.length, ...customer };
    return {
      ...state,
      customer: customerWithId,
    };
  },
  [customerDetailsDone]: state => {
    const { callerInfo, customer } = state;
    const contacts = get(callerInfo, 'contacts', []);
    const newContacts = [...contacts];

    if (!contacts.length || !contacts.find(contact => isEqual(customer, contact))) {
      newContacts.push(customer);
    }

    return {
      ...state,
      callerInfo: {
        ...callerInfo,
        contacts: newContacts,
      },
    };
  },
  [deliveryDetailsDone]: state => {
    const { callerInfo, address } = state;
    const addresses = get(callerInfo, 'addresses', []);
    const newAddresses = [...addresses];

    if ((!addresses.length || !addresses.find(item => isEqual(address, item))) && !!Object.keys(address).length) {
      newAddresses.push({ id: addressesCounter++, ...address });
    }

    return newAddresses.length !== addresses.length
      ? {
          ...state,
          address: {
            ...newAddresses[newAddresses.length - 1],
          },
          callerInfo: {
            ...callerInfo,
            addresses: newAddresses,
          },
        }
      : state;
  },
  [updateDeliveryAddress]: (state, payload) => {
    const { address } = payload;

    return {
      ...state,
      address,
    };
  },
  [updateDeliveryType]: (state, payload) => {
    const { deliveryType } = payload;
    return {
      ...state,
      deliveryType,
    };
  },
  [menuQuery]: (state, payload) => {
    const { query } = payload;
    return {
      ...state,
      query,
    };
  },
  [toggleMenuFilter]: (state, payload) => {
    const { filter } = payload;
    const filters = {
      ...state.filters,
    };

    if (filters[filter]) {
      delete filters[filter];
    } else {
      filters[filter] = true;
    }

    return {
      ...state,
      filters,
    };
  },
  [clearFilters]: state => {
    return {
      ...state,
      filters: {},
    };
  },
  [cartUpsert]: (state, payload) => {
    const { orderItem, cartIndex } = payload;
    const previousOrderItems = [...state.orderItems];
    const orderItems =
      cartIndex === NEW_DISH_INDEX
        ? [...state.orderItems, orderItem]
        : replaceItem(state.orderItems, cartIndex, orderItem);

    return {
      ...state,
      orderItems,
      previousOrderItems,
    };
  },
  [orderItemChangeCount]: (state, payload) => {
    const { cartIndex, count } = payload;
    const previousOrderItems = [...state.orderItems];
    const orderItem = state.orderItems[cartIndex];
    const toAdd = changeCount({ orderItem, count });

    const orderItems =
      count === 0 ? removeItem(state.orderItems, cartIndex) : replaceItem(state.orderItems, cartIndex, toAdd);

    return {
      ...state,
      orderItems,
      previousOrderItems,
    };
  },
  [cancelOrder]: () => initialState,
  [fetchOrderHistorySuccess]: (state, payload) => {
    return {
      ...state,
      orderHistory: payload.orders,
      orderHistoryDisplayable: payload.displayableOrders,
    };
  },
  [setUnavailableItems]: (state, payload) => {
    return {
      ...state,
      unavailableOrderItems: payload.items,
    };
  },
  [setPaymentAssociations]: (state, payload) => {
    const { paymentAssociations } = payload;
    return {
      ...state,
      paymentAssociations,
    };
  },
  [addComment]: (state, payload) => {
    const comment = new CustomerComment({ ...payload });
    const latestComment = getLatestComment(state.comments);
    const newState = { ...state };
    if (latestComment.text !== comment.text && comment.text !== '') {
      newState.comments = pushItem(state.comments, comment);
      newState.hasUnsavedComments = true;
    }
    return newState;
  },
  [setManagerDiscount]: (state, payload) => {
    const managerDiscount = createManagerDiscount(state.managerDiscount, payload);
    return {
      ...state,
      managerDiscount,
    };
  },
  [resetManagerDiscount]: (state) => {
    return {
      ...state,
      managerDiscount: {
        ...state.managerDiscount,
        amount: 0,
        currencyAmount: 0,
        percentAmount: 0,
      },
    };
  },
  [clearCustomerSession]: state => {
    return {
      ...state,
      ...newCustomerState,
    };
  },
  [setCommentsServerTimestamp]: (state, payload) => {
    const { commentsServerTimestamp } = payload;
    return {
      ...state,
      commentsServerTimestamp,
    };
  },
  [setHasUnsavedComments]: (state, payload) => {
    const { hasUnsavedComments } = payload;
    return {
      ...state,
      hasUnsavedComments,
    };
  },
  [changeOrderComment]: (state, payload) => {
    const comment = payload.value;
    return {
      ...state,
      comment,
    };
  },
};

export default createReducer(initialState, handlers);
